import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEapiAuth } from "@/hooks/useEapiAuth";
import { mapEapiStatusToOrderStatus, isForwardTransition } from "@/lib/eapi-status-mapper";
import {
  computeNextParentStatus,
  shouldAdvanceTripStatus,
  mayUseOrderLevelTrip,
  chunkIds,
} from "@/lib/parent-status-from-legs";
import { resolveLegStatus } from "@/lib/leg-status-resolve";
import type { OrderStatus } from "@/types/tms";
import { useTenant } from "@/hooks/useTenant";

interface SyncableOrder {
  id: string;
  eapi_trip_id?: string | null;
  status: OrderStatus;
  tenant_id?: string;
  eapi_eta?: string | null;
}

interface LegTripRow {
  tripRowId: string;
  legId: string;
  orderId: string;
  eapiTripId: string;
  eapiStatus: string | null;
  driverStatus: string | null;
}

/**
 * On-demand EAPI status sync.
 *
 * Per-leg first: each leg's own active trip is polled and mirrored onto
 * `trips.eapi_status`, and the parent order only moves when ALL of its live
 * legs say so (`computeNextParentStatus`). The old behaviour — promoting the
 * parent straight off `orders.eapi_trip_id`, which on a multi-leg order is
 * only a copy of leg 1 — marked whole orders delivered while later legs had
 * never started (AOP-HTSH-06110).
 *
 * Orders with no leg trips at all fall back to the single order-level trip.
 */
export function useEapiStatusSync(orders: SyncableOrder[] | undefined) {
  const { eapiClient, isReady } = useEapiAuth();
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const lastSyncKey = useRef<string>("");
  const syncInFlight = useRef(false);

  useEffect(() => {
    if (!orders || orders.length === 0 || !eapiClient || !isReady || syncInFlight.current) return;

    // Never touch a terminal order.
    const syncable = orders.filter(
      (o) => o.status !== "delivered" && o.status !== "canceled",
    );
    if (syncable.length === 0) return;

    // Deduplicate by creating a stable key from order IDs + statuses
    const key = syncable.map((o) => `${o.id}:${o.status}`).join(",");
    if (key === lastSyncKey.current) return;
    lastSyncKey.current = key;

    syncInFlight.current = true;

    (async () => {
      try {
        const orderIds = syncable.map((o) => o.id);

        // Active per-leg trips + every leg for these orders. Chunked so a long
        // list can't overflow the request URL, and fail closed: if any lookup
        // fails we write nothing this run. A silent failure here used to send
        // every order down the leg-1-only fallback and mark multi-leg orders
        // delivered off leg 1 (AOP-DETM-02576).
        const tripRows: any[] = [];
        const legRows: any[] = [];
        for (const ids of chunkIds(orderIds)) {
          const [tripsRes, legsRes] = await Promise.all([
            supabase
              .from("trips")
              .select("id, eapi_trip_id, eapi_status, leg_id, legs!inner(id, order_id, driver_status)")
              .eq("is_active", true)
              .not("eapi_trip_id", "is", null)
              .in("legs.order_id", ids),
            supabase.from("legs").select("id, order_id, driver_status").in("order_id", ids),
          ]);
          if (tripsRes.error || legsRes.error) {
            console.warn("[EapiStatusSync] Lookup failed; skipping this run:", tripsRes.error ?? legsRes.error);
            return;
          }
          tripRows.push(...(tripsRes.data ?? []));
          legRows.push(...(legsRes.data ?? []));
        }

        const legTripsByOrder = new Map<string, LegTripRow[]>();
        for (const row of tripRows) {
          const orderId = row.legs?.order_id as string | undefined;
          if (!orderId) continue;
          const entry: LegTripRow = {
            tripRowId: row.id,
            legId: row.leg_id,
            orderId,
            eapiTripId: row.eapi_trip_id,
            eapiStatus: row.eapi_status ?? null,
            driverStatus: row.legs?.driver_status ?? null,
          };
          const list = legTripsByOrder.get(orderId) ?? [];
          list.push(entry);
          legTripsByOrder.set(orderId, list);
        }

        const legsByOrder = new Map<string, Array<{ id: string; driver_status: string | null }>>();
        for (const l of legRows) {
          const list = legsByOrder.get(l.order_id) ?? [];
          list.push({ id: l.id, driver_status: l.driver_status ?? null });
          legsByOrder.set(l.order_id, list);
        }

        // Trip ids to poll: every active leg trip, plus the order-level trip
        // for orders that have none (legacy single-trip rows).
        const tripIds = new Set<string>();
        for (const o of syncable) {
          const legTrips = legTripsByOrder.get(o.id) ?? [];
          if (legTrips.length > 0) legTrips.forEach((t) => tripIds.add(t.eapiTripId));
          else if (o.eapi_trip_id && mayUseOrderLevelTrip(legsByOrder.get(o.id)?.length ?? 0)) {
            tripIds.add(o.eapi_trip_id);
          }
        }
        if (tripIds.size === 0) return;

        const res = await eapiClient.getTripStatuses([...tripIds]);
        const tripStatuses = res?.data?.statuses || (Array.isArray(res?.data) ? res.data : null);
        if (!tripStatuses || !Array.isArray(tripStatuses)) {
          console.warn("[EapiStatusSync] Unexpected response:", res);
          return;
        }

        /** eapi trip id → { mapped order status, raw stage, eta } */
        const byTripId = new Map<string, { mapped: string | null; stage: string; eta: string | null }>();
        for (const trip of tripStatuses) {
          const tripId = trip.id || trip.trip_id;
          const stage = trip.stage || trip.status;
          if (!tripId || !stage) continue;
          byTripId.set(tripId, {
            mapped: mapEapiStatusToOrderStatus(stage),
            stage,
            eta: stage === "enroute" && trip.eta ? trip.eta : null,
          });
        }

        let changed = false;
        let listChanged = false;

        for (const order of syncable) {
          const legTrips = legTripsByOrder.get(order.id) ?? [];
          const nowIso = new Date().toISOString();

          let nextParent: string | null = null;
          let eapiEta: string | null = null;

          if (legTrips.length > 0) {
            // 1. Mirror each leg trip's status onto its own row.
            const mirrored = new Map<string, string | null>();
            for (const lt of legTrips) {
              const upstream = byTripId.get(lt.eapiTripId);
              if (!upstream) {
                mirrored.set(lt.legId, lt.eapiStatus);
                continue;
              }
              if (upstream.eta) eapiEta = upstream.eta;
              if (shouldAdvanceTripStatus(lt.eapiStatus, upstream.mapped)) {
                const { error } = await supabase
                  .from("trips")
                  .update({ eapi_status: upstream.mapped })
                  .eq("id", lt.tripRowId);
                if (error) {
                  console.error(`[EapiStatusSync] Trip ${lt.tripRowId} update failed:`, error);
                  mirrored.set(lt.legId, lt.eapiStatus);
                  continue;
                }
                changed = true;
                mirrored.set(lt.legId, upstream.mapped);
              } else {
                mirrored.set(lt.legId, lt.eapiStatus);
              }
            }

            // 2. Decide the parent from EVERY leg, trip or not.
            const allLegs = legsByOrder.get(order.id) ?? legTrips.map((t) => ({
              id: t.legId,
              driver_status: t.driverStatus,
            }));
            const legStatuses = allLegs.map((leg) =>
              resolveLegStatus(
                {
                  driver_status: leg.driver_status,
                  eapi_status: mirrored.get(leg.id) ?? null,
                },
                undefined,
                allLegs.length > 1,
              ) ?? "booked",
            );
            nextParent = computeNextParentStatus(order.status, legStatuses);
          } else if (!mayUseOrderLevelTrip(legsByOrder.get(order.id)?.length ?? 0)) {
            // Has legs but none with a live trip: nothing upstream to read.
            // Never fall back to orders.eapi_trip_id (a leg-1 copy).
            continue;
          } else {
            // Legacy single order-level trip (order has no legs at all).
            const upstream = order.eapi_trip_id ? byTripId.get(order.eapi_trip_id) : undefined;
            if (!upstream) continue;
            eapiEta = upstream.eta;
            nextParent =
              upstream.mapped && isForwardTransition(order.status, upstream.mapped as OrderStatus)
                ? upstream.mapped
                : null;
          }

          const statusChanged = !!nextParent;
          const updatePayload: Record<string, unknown> = {};
          if (statusChanged) updatePayload.status = nextParent;
          if (eapiEta !== order.eapi_eta) updatePayload.eapi_eta = eapiEta;
          if (nextParent === "delivered") updatePayload.eapi_eta = null;

          // Write-once actual timestamps, kept write-once by the optimistic
          // lock below.
          if (statusChanged && nextParent === "in_transit") {
            updatePayload.actual_departure_time = nowIso;
          }
          if (statusChanged && nextParent === "delivered") {
            updatePayload.actual_arrival_time = nowIso;
            if (order.status !== "in_transit") {
              updatePayload.actual_departure_time = nowIso;
            }
          }

          if (Object.keys(updatePayload).length === 0) continue;

          const query = supabase.from("orders").update(updatePayload).eq("id", order.id);
          if (statusChanged) query.eq("status", order.status);

          const { error } = await query;
          if (error) {
            console.error(`[EapiStatusSync] Failed to update order ${order.id}:`, error);
            continue;
          }

          if (statusChanged) {
            listChanged = true;
            await supabase.from("order_change_log").insert({
              order_id: order.id,
              field_name: "status",
              old_value: order.status,
              new_value: nextParent!,
              changed_by: "System (EAPI)",
              ...(order.tenant_id ? { tenant_id: order.tenant_id } : {}),
            });

            if (updatePayload.actual_departure_time) {
              await supabase.from("order_change_log").insert({
                order_id: order.id,
                field_name: "actual_departure_time",
                old_value: null,
                new_value: nowIso,
                changed_by: "System (EAPI)",
                ...(order.tenant_id ? { tenant_id: order.tenant_id } : {}),
              });
            }
            if (updatePayload.actual_arrival_time) {
              await supabase.from("order_change_log").insert({
                order_id: order.id,
                field_name: "actual_arrival_time",
                old_value: null,
                new_value: nowIso,
                changed_by: "System (EAPI)",
                ...(order.tenant_id ? { tenant_id: order.tenant_id } : {}),
              });
            }
          }

          changed = true;
          console.log(
            `[EapiStatusSync] Order ${order.id}: ${order.status} → ${statusChanged ? nextParent : "(eta only)"}`,
          );
        }

        if (changed) {
          if (listChanged) {
            queryClient.invalidateQueries({ queryKey: ["orders", tenantId] });
          }
          queryClient.invalidateQueries({ queryKey: ["order-detail"] });
          queryClient.invalidateQueries({ queryKey: ["order-children-legs"] });
          queryClient.invalidateQueries({ queryKey: ["schedule-orders"] });
        }
      } catch (err) {
        console.error("[EapiStatusSync] Error:", err);
      } finally {
        syncInFlight.current = false;
      }
    })();
  }, [orders, eapiClient, isReady, queryClient, tenantId]);
}
